/* Thin wrapper around window.gtag so components stay clean.
   Safe to call before gtag loads — no-ops if unavailable. */

// Google Ads conversion action — "Booking Completed" (original, feeds existing campaigns)
const ADS_CONVERSION_ID = "AW-17966590669";
const ADS_CONVERSION_LABEL = "-Wu3CPurnNIcEM3VkfdC";
const ADS_CONVERSION_SEND_TO = `${ADS_CONVERSION_ID}/${ADS_CONVERSION_LABEL}`;

// Second conversion action — "Book appointment (3)", created for the fall 2026
// campaign landing pages. Fires alongside the original; the Ads-provided snippet
// uses a fixed value of 1.0 CAD. Owner marks one Primary / one Secondary in the
// Google Ads UI so bookings aren't double-counted for bidding.
const ADS_CONVERSION_SEND_TO_FALL = "AW-17966590669/W7PHCLbLrZIdEM3VkfdC";

export function track(eventName, params = {}) {
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", eventName, params);
    }
  } catch (e) {
    // Never let analytics errors break the app
  }
}

/**
 * Normalize a phone number to E.164 (+1XXXXXXXXXX) — required for Enhanced
 * Conversions matching. Handles the common HRM/NS formats:
 *   "902-555-1234", "(902) 555 1234", "9025551234", "+1 902-555-1234"
 * Returns the input untouched if we can't confidently normalize it.
 */
function toE164(raw) {
  if (!raw) return "";
  const digits = String(raw).replace(/\D/g, "");
  if (!digits) return "";
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  if (digits.length === 10) return `+1${digits}`;
  return String(raw).trim().startsWith("+") ? String(raw).trim() : `+${digits}`;
}

/** Split "John Doe" -> { first_name: "John", last_name: "Doe" } */
function splitName(full) {
  const parts = String(full || "").trim().split(/\s+/);
  return {
    first_name: parts[0] || "",
    last_name: parts.slice(1).join(" ") || "",
  };
}

/**
 * Build the user_data payload Google Ads expects for Enhanced Conversions.
 * gtag will hash email + phone automatically before transmission.
 * Only include fields we actually have — Google recommends passing as much as possible.
 */
function buildUserData({ email, phone, name, address } = {}) {
  const ud = {};
  if (email && /\S+@\S+\.\S+/.test(email)) {
    ud.email = email.trim().toLowerCase();
  }
  if (phone) {
    const normalized = toE164(phone);
    if (normalized) ud.phone_number = normalized;
  }
  if (name) {
    const { first_name, last_name } = splitName(name);
    ud.address = ud.address || {};
    if (first_name) ud.address.first_name = first_name;
    if (last_name) ud.address.last_name = last_name;
  }
  if (address) {
    ud.address = ud.address || {};
    ud.address.street = address;
    ud.address.country = "CA";
    ud.address.region = "NS";
  }
  return ud;
}

/**
 * Fire a Google Ads conversion with Enhanced Conversions data.
 * Called after a successful booking.
 */
export function trackAdsBookingConversion({ value = 0, currency = "CAD", transactionId, userData } = {}) {
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      const payload = {
        send_to: ADS_CONVERSION_SEND_TO,
        value: Number(value) || 0,
        currency,
        transaction_id: transactionId || "",
      };
      const ud = buildUserData(userData || {});
      if (Object.keys(ud).length > 0) {
        payload.user_data = ud;
      }
      window.gtag("event", "conversion", payload);
      // Fall campaign action — fixed value 1.0 CAD per the Ads snippet, same
      // transaction_id so refreshes/re-submits dedupe within the action.
      const fallPayload = {
        send_to: ADS_CONVERSION_SEND_TO_FALL,
        value: 1.0,
        currency: "CAD",
        transaction_id: transactionId || "",
      };
      if (Object.keys(ud).length > 0) {
        fallPayload.user_data = ud;
      }
      window.gtag("event", "conversion", fallPayload);
    }
  } catch (e) {
    // Silently swallow — analytics must never break the booking flow
  }
}

/* Standard conversion events — align with what Google Analytics/Ads expects */
export const trackBookingSubmitted = ({ value, currency = "CAD", numVehicles = 1, service, transactionId, userData } = {}) => {
  track("booking_submitted", {
    value: Number(value) || 0,
    currency,
    num_vehicles: numVehicles,
    service_name: service || "",
  });
  // Also fire the Google Ads conversion — this is the money event.
  trackAdsBookingConversion({ value, currency, transactionId, userData });
};

export const trackGenerateLead = ({ value, currency = "CAD" } = {}) =>
  track("generate_lead", {
    value: Number(value) || 0,
    currency,
  });

export const trackContactSubmitted = () => {
  track("contact_submitted", {});
  fbq("Contact", {});
};

export const trackNewsletterSignup = () => {
  track("newsletter_signup", {});
  fbq("Lead", { content_name: "Newsletter Signup" });
};

/**
 * Track a phone-number click on the site.
 * Fires:
 *   1. GA4 event "phone_click" — can be imported into Google Ads as a
 *      conversion action (Ads UI → Conversions → Import → Google Analytics).
 *   2. Meta Pixel "Contact" event — signals lead intent to Facebook.
 *
 * @param {object} params - Optional location metadata ("header" | "hero" | "contact" | "footer" | "mobile")
 */
export const trackPhoneClick = ({ location = "unknown" } = {}) => {
  track("phone_click", { location, phone: "782-321-9302" });
  fbq("Contact", { content_name: `Phone Click - ${location}` });
};

/* ============ Meta Pixel wrappers ============ */

/** Thin wrapper around window.fbq — safe to call before Pixel loads. */
export function fbq(eventName, params = {}) {
  try {
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      window.fbq("track", eventName, params);
    }
  } catch (e) {
    /* never break the app for tracking */
  }
}

/** Fire on SPA route changes so Meta sees each page view separately. */
export function fbqPageView() {
  try {
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      window.fbq("track", "PageView");
    }
  } catch (e) { /* noop */ }
}

/** Fire when a customer starts the booking flow (Booking.jsx step 1). */
export function fbqInitiateCheckout({ value = 0, currency = "CAD", service = "" } = {}) {
  fbq("InitiateCheckout", {
    value: Number(value) || 0,
    currency,
    content_name: service,
    content_category: "auto-detailing",
  });
}

/** Fire when they land on a service detail page. */
export function fbqViewContent({ value = 0, currency = "CAD", contentName = "", contentId = "" } = {}) {
  fbq("ViewContent", {
    value: Number(value) || 0,
    currency,
    content_name: contentName,
    content_ids: contentId ? [contentId] : [],
    content_category: "auto-detailing",
    content_type: "product",
  });
}

/** Fire when a booking is successfully completed. Uses the Lead standard event. */
export function fbqBookingLead({ value = 0, currency = "CAD", numVehicles = 1, service = "", transactionId = "" } = {}) {
  fbq("Lead", {
    value: Number(value) || 0,
    currency,
    content_name: service,
    content_category: "auto-detailing",
    num_items: numVehicles,
    order_id: transactionId,
  });
}
